import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import FileUploader from "@/components/FileUploader";
import { eur } from "@/lib/constants";
import { toast } from "sonner";

const STEPS = ["Votre besoin", "Votre projet", "Vos fichiers", "Informations techniques", "Options", "Récapitulatif", "Confirmation"];

export default function OrderWizard() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [svc, setSvc] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ deadline: "", printer_specs: "" });
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [technical, setTechnical] = useState({ dimensions: "", side: "recto", destination: "print", pages: "", format: "" });
  const [selectedOptions, setSelectedOptions] = useState([]);

  useEffect(() => { api.get(`/services/${slug}`).then((r) => setSvc(r.data)); }, [slug]);

  const total = useMemo(() => {
    if (!svc) return 0;
    const opt = (svc.options || []).filter((o) => selectedOptions.includes(o.label)).reduce((a, b) => a + (b.price || 0), 0);
    return (svc.starting_price || 0) + opt;
  }, [svc, selectedOptions]);

  if (!svc) return <div className="min-h-[60vh] flex items-center justify-center font-mono text-sm">Chargement…</div>;

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    try {
      const { data } = await api.post("/orders", {
        service_slug: slug,
        description,
        answers,
        technical,
        options: selectedOptions,
        file_ids: files.map((f) => f.id),
      });
      toast.success(`Commande créée · ${data.reference}`);
      setStep(STEPS.length - 1);
      setTimeout(() => nav(`/espace/commande/${data.id}`), 900);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="cs-cream py-14 min-h-[80vh]" data-testid="page-order-wizard">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          <div className="cs-eyebrow text-neutral-500">Commander · {svc.category}</div>
          <h1 className="cs-h1 mt-3 text-4xl lg:text-5xl">{svc.name}</h1>

          {/* Stepper */}
          <ol className="mt-8 flex flex-wrap gap-x-4 gap-y-2 text-xs font-mono">
            {STEPS.map((s, i) => (
              <li key={i} className={`flex items-center gap-2 ${i === step ? "text-[#0E7C90]" : i < step ? "text-black" : "text-neutral-400"}`}>
                {i < step ? <Check size={14} /> : <span className="inline-block w-4 text-center">{i + 1}</span>}
                <span className="uppercase tracking-widest">{s}</span>
                {i < STEPS.length - 1 && <ChevronRight size={12} className="text-neutral-400" />}
              </li>
            ))}
          </ol>

          <div className="mt-10 cs-card p-8" data-testid={`wizard-step-${step}`}>
            {step === 0 && (
              <div>
                <h2 className="cs-h3 text-2xl">Votre besoin</h2>
                <p className="mt-2 text-sm text-neutral-600">Quelques précisions pour cadrer la prestation.</p>
                <label className="cs-eyebrow text-neutral-500 mt-6 block">Délai souhaité</label>
                <input value={answers.deadline} onChange={(e) => setAnswers({ ...answers, deadline: e.target.value })} className="cs-input mt-2" placeholder="Ex : sous 5 jours ouvrés" data-testid="wizard-deadline" />
                <label className="cs-eyebrow text-neutral-500 mt-6 block">Consignes de votre imprimeur (si vous en avez)</label>
                <textarea rows={4} value={answers.printer_specs} onChange={(e) => setAnswers({ ...answers, printer_specs: e.target.value })} className="cs-input mt-2" placeholder="Collez ici les spécifications reçues" data-testid="wizard-printer-specs" />
              </div>
            )}
            {step === 1 && (
              <div>
                <h2 className="cs-h3 text-2xl">Votre projet</h2>
                <p className="mt-2 text-sm text-neutral-600">Décrivez votre projet en quelques lignes.</p>
                <textarea rows={7} value={description} onChange={(e) => setDescription(e.target.value)} className="cs-input mt-4" placeholder="Nature du projet, contexte, contraintes…" data-testid="wizard-description" />
              </div>
            )}
            {step === 2 && (
              <div>
                <h2 className="cs-h3 text-2xl">Vos fichiers</h2>
                <p className="mt-2 text-sm text-neutral-600">Glissez vos fichiers sources et les documents utiles.</p>
                <div className="mt-4"><FileUploader files={files} setFiles={setFiles} testId="wizard-uploader" /></div>
              </div>
            )}
            {step === 3 && (
              <div>
                <h2 className="cs-h3 text-2xl">Informations techniques</h2>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="cs-eyebrow text-neutral-500">Dimensions finales</label>
                    <input value={technical.dimensions} onChange={(e) => setTechnical({ ...technical, dimensions: e.target.value })} className="cs-input mt-2" placeholder="Ex : 148 × 210 mm" data-testid="wizard-dim" />
                  </div>
                  <div>
                    <label className="cs-eyebrow text-neutral-500">Recto / Verso</label>
                    <select value={technical.side} onChange={(e) => setTechnical({ ...technical, side: e.target.value })} className="cs-input mt-2" data-testid="wizard-side">
                      <option value="recto">Recto seul</option>
                      <option value="recto_verso">Recto / Verso</option>
                    </select>
                  </div>
                  <div>
                    <label className="cs-eyebrow text-neutral-500">Destination</label>
                    <select value={technical.destination} onChange={(e) => setTechnical({ ...technical, destination: e.target.value })} className="cs-input mt-2" data-testid="wizard-destination">
                      <option value="print">Impression</option>
                      <option value="numerique">Numérique</option>
                      <option value="mixte">Les deux</option>
                    </select>
                  </div>
                  <div>
                    <label className="cs-eyebrow text-neutral-500">Nombre de pages</label>
                    <input value={technical.pages} onChange={(e) => setTechnical({ ...technical, pages: e.target.value })} className="cs-input mt-2" placeholder="Ex : 24" data-testid="wizard-pages" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="cs-eyebrow text-neutral-500">Format final / Imprimeur</label>
                    <input value={technical.format} onChange={(e) => setTechnical({ ...technical, format: e.target.value })} className="cs-input mt-2" placeholder="Ex : PDF X-1a — imprimeur BookBeo" data-testid="wizard-format" />
                  </div>
                </div>
              </div>
            )}
            {step === 4 && (
              <div>
                <h2 className="cs-h3 text-2xl">Options</h2>
                {svc.options?.length ? (
                  <ul className="mt-4 divide-y divide-black/10">
                    {svc.options.map((o) => (
                      <li key={o.label} className="py-3 flex items-center justify-between">
                        <label className="flex items-center gap-3">
                          <input type="checkbox" checked={selectedOptions.includes(o.label)} onChange={(e) => {
                            setSelectedOptions((prev) => e.target.checked ? [...prev, o.label] : prev.filter((x) => x !== o.label));
                          }} data-testid={`wizard-option-${o.label}`} />
                          <span>{o.label}</span>
                        </label>
                        <span className="font-mono text-sm">+ {eur(o.price)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-neutral-600">Aucune option pour cette prestation.</p>
                )}
              </div>
            )}
            {step === 5 && (
              <div>
                <h2 className="cs-h3 text-2xl">Récapitulatif</h2>
                <dl className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                  <div><dt className="cs-eyebrow text-neutral-500">Prestation</dt><dd className="mt-1">{svc.name}</dd></div>
                  <div><dt className="cs-eyebrow text-neutral-500">Délai estimatif</dt><dd className="mt-1">{svc.delay}</dd></div>
                  <div className="sm:col-span-2"><dt className="cs-eyebrow text-neutral-500">Description</dt><dd className="mt-1 whitespace-pre-wrap">{description || "—"}</dd></div>
                  <div className="sm:col-span-2"><dt className="cs-eyebrow text-neutral-500">Fichiers</dt><dd className="mt-1">{files.map((f) => f.original_name).join(", ") || "—"}</dd></div>
                  <div className="sm:col-span-2"><dt className="cs-eyebrow text-neutral-500">Options</dt><dd className="mt-1">{selectedOptions.join(", ") || "Aucune"}</dd></div>
                </dl>
                <div className="mt-6 border-t border-black/10 pt-4 flex items-end justify-between">
                  <div>
                    <div className="cs-eyebrow text-neutral-500">Total estimatif</div>
                    <div className="font-serif text-4xl">{eur(total)}</div>
                    <p className="mt-2 text-xs text-neutral-500 max-w-md">
                      Une prestation au tarif fixe ne couvre que le périmètre annoncé. Si nécessaire, Charbonier Studio proposera une estimation complémentaire.
                    </p>
                  </div>
                  <button onClick={submit} className="cs-btn-primary" data-testid="wizard-submit">
                    Confirmer la commande <ArrowUpRight size={18} />
                  </button>
                </div>
              </div>
            )}
            {step === 6 && (
              <div className="text-center py-8">
                <div className="cs-eyebrow text-[#0E7C90]">Commande envoyée</div>
                <h2 className="cs-h2 mt-3 text-4xl">Merci.</h2>
                <p className="mt-3 text-neutral-600">Votre demande a bien été transmise à Charbonier Studio.</p>
                <Link to="/espace" className="cs-btn-primary mt-6 inline-flex">Voir mes commandes</Link>
              </div>
            )}
          </div>

          {step < 5 && (
            <div className="mt-6 flex items-center justify-between">
              <button onClick={prev} disabled={step === 0} className="text-sm cs-link-underline disabled:opacity-40" data-testid="wizard-prev">← Précédent</button>
              <button onClick={next} className="cs-btn-primary" data-testid="wizard-next">Continuer <ArrowUpRight size={18} /></button>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="cs-card p-6 sticky top-24">
            <div className="cs-eyebrow text-neutral-500">Résumé</div>
            <div className="mt-3 font-serif text-2xl">{svc.name}</div>
            <div className="mt-4 flex justify-between text-sm border-t border-black/10 pt-3">
              <span>Prix de base</span><span className="font-mono">{eur(svc.starting_price)}</span>
            </div>
            {selectedOptions.map((label) => {
              const o = svc.options.find((x) => x.label === label);
              return (
                <div key={label} className="mt-2 flex justify-between text-sm">
                  <span>{label}</span><span className="font-mono">+ {eur(o.price)}</span>
                </div>
              );
            })}
            <div className="mt-4 border-t border-black/10 pt-3 flex justify-between items-baseline">
              <span className="cs-eyebrow text-neutral-500">Total</span>
              <span className="font-serif text-3xl">{eur(total)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
